import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Menu, X, User, LogOut, Package, FileText, ShoppingCart, Settings, Globe, Mail, Phone, MapPin, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { NewsletterInline } from './Newsletter';
import { LanguageSelector } from './LanguageSelector';

// Professional Logo Component
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/bb052137-473a-4b30-a3c1-0ea12ef31e4c/images/7abd8caee89cf0ce06f3afca1b73ae3c501495291f4c4f8d03aaba6f122e597e.png";

const Logo = ({ size = 'default', light = false }) => {
  const sizes = {
    small: { container: 'w-8 h-8', brand: 'text-lg', sub: 'text-[8px]' },
    default: { container: 'w-10 h-10', brand: 'text-xl', sub: 'text-[9px]' },
    large: { container: 'w-12 h-12', brand: 'text-2xl', sub: 'text-[10px]' }
  };
  const s = sizes[size];
  
  return (
    <div className="flex items-center gap-3 shrink-0">
      <img 
        src={LOGO_URL} 
        alt="Afriqoma Logo" 
        className={`${s.container} object-contain rounded-lg shrink-0`}
      />
      <div className="flex flex-col leading-tight shrink-0">
        <span 
          className={`font-black ${s.brand} tracking-wide`}
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            background: 'linear-gradient(135deg, #B8860B 0%, #D4A853 50%, #B8860B 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: '1.1'
          }}
        >
          AFRIQOMA
        </span>
        <span 
          className={`${s.sub} font-semibold tracking-widest uppercase`}
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            color: light ? 'rgba(255,255,255,0.8)' : '#666666',
            lineHeight: '1.2'
          }}
        >
          Global Trade Hub
        </span>
      </div>
    </div>
  );
};

export const Header = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 ml-8">
            <Link to="/products" className="px-4 py-2 text-slate hover:text-primary hover:bg-primary/5 rounded-lg transition-all font-medium" data-testid="nav-products">
              {t('common.products', 'Products')}
            </Link>
            <Link to="/suppliers" className="px-4 py-2 text-slate hover:text-primary hover:bg-primary/5 rounded-lg transition-all font-medium" data-testid="nav-suppliers">
              {t('common.suppliers', 'Suppliers')}
            </Link>
            <Link to="/categories" className="px-4 py-2 text-slate hover:text-primary hover:bg-primary/5 rounded-lg transition-all font-medium" data-testid="nav-categories">
              {t('common.categories', 'Categories')}
            </Link>
            <Link to="/tracking" className="px-4 py-2 text-slate hover:text-primary hover:bg-primary/5 rounded-lg transition-all font-medium" data-testid="nav-tracking">
              {t('nav.tracking', 'Track Package')}
            </Link>
            <Link to="/about" className="px-4 py-2 text-slate hover:text-primary hover:bg-primary/5 rounded-lg transition-all font-medium" data-testid="nav-about">
              {t('nav.about', 'About')}
            </Link>
          </nav>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-3">
            {/* Language Selector */}
            <LanguageSelector variant="compact" />
            
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 hover:bg-primary/5" data-testid="user-menu-trigger">
                    <div className="w-9 h-9 bg-gradient-to-br from-primary to-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                      {user?.full_name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium">{user?.full_name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-3 bg-gradient-to-br from-primary/5 to-orange-50 rounded-t-md -mt-1 -mx-1">
                    <p className="font-heading font-bold text-charcoal">{user?.full_name}</p>
                    <p className="text-sm text-slate">{user?.company_name}</p>
                    <span className="inline-block mt-1 text-xs bg-primary text-white px-2 py-0.5 rounded-full capitalize">{user?.role}</span>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <Settings className="w-4 h-4 mr-2" /> {t('common.dashboard', 'Dashboard')}
                  </DropdownMenuItem>
                  {user?.role === 'buyer' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/rfq')} data-testid="menu-rfq">
                        <FileText className="w-4 h-4 mr-2" /> {t('nav.myRFQs', 'My RFQs')}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/dashboard/orders')} data-testid="menu-orders">
                        <ShoppingCart className="w-4 h-4 mr-2" /> {t('common.orders', 'Orders')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'supplier' && (
                    <DropdownMenuItem onClick={() => navigate('/dashboard/products')} data-testid="menu-products">
                      <Package className="w-4 h-4 mr-2" /> {t('nav.myProducts', 'My Products')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t('common.logout', 'Sign Out')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="font-medium hover:bg-primary/5" data-testid="login-btn">{t('common.login', 'Sign In')}</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-primary to-orange-500 hover:from-primary-hover hover:to-orange-600 text-white font-semibold shadow-lg shadow-primary/25 px-6" data-testid="register-btn">
                    {t('common.getStarted', 'Get Started')}
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t bg-white">
            <nav className="flex flex-col gap-2">
              <Link to="/products" className="px-4 py-3 text-slate hover:text-primary hover:bg-primary/5 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                Products
              </Link>
              <Link to="/suppliers" className="px-4 py-3 text-slate hover:text-primary hover:bg-primary/5 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                Suppliers
              </Link>
              <Link to="/categories" className="px-4 py-3 text-slate hover:text-primary hover:bg-primary/5 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                Categories
              </Link>
              <Link to="/about" className="px-4 py-3 text-slate hover:text-primary hover:bg-primary/5 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                About
              </Link>
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="px-4 py-3 text-slate hover:text-primary hover:bg-primary/5 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Dashboard
                  </Link>
                  <button onClick={handleLogout} className="px-4 py-3 text-left text-red-600 hover:bg-red-50 rounded-lg font-medium">
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="flex gap-2 pt-2 px-2">
                  <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">Sign In</Button>
                  </Link>
                  <Link to="/register" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-gradient-to-r from-primary to-orange-500 text-white">Get Started</Button>
                  </Link>
                </div>
              )}
              
              {/* Mobile Language Selector */}
              <div className="px-4 pt-4 border-t mt-2">
                <p className="text-xs text-slate mb-2 font-medium">Language</p>
                <LanguageSelector variant="default" showLabel={true} />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-charcoal text-bone relative overflow-hidden" data-testid="footer">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent rounded-full blur-3xl" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1 space-y-6">
            <Logo light={true} />
            <p className="text-slate text-sm leading-relaxed">
              Africa's premier B2B marketplace connecting verified manufacturers with global buyers. Building bridges for sustainable trade.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors">
                <Globe className="w-5 h-5" />
              </a>
              <a href="mailto:service@afriqoma.com" className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/products" className="text-slate hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 bg-primary rounded-full"></span>Browse Products</Link></li>
              <li><Link to="/suppliers" className="text-slate hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 bg-primary rounded-full"></span>Find Suppliers</Link></li>
              <li><Link to="/categories" className="text-slate hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 bg-primary rounded-full"></span>Categories</Link></li>
              <li><Link to="/guides" className="text-slate hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 bg-primary rounded-full"></span>Guides & Resources</Link></li>
              <li><Link to="/about" className="text-slate hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 bg-primary rounded-full"></span>About Us</Link></li>
            </ul>
          </div>

          {/* Newsletter Signup */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Newsletter
            </h4>
            <p className="text-slate text-sm mb-4">
              Get monthly AI-curated insights on African trade opportunities.
            </p>
            <NewsletterInline />
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-white">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-xs text-slate uppercase tracking-wider">General</p>
                  <a href="mailto:service@afriqoma.com" className="text-white hover:text-primary transition-colors text-sm">service@afriqoma.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <p className="text-xs text-slate uppercase tracking-wider">Support</p>
                  <a href="mailto:support@afriqoma.com" className="text-white hover:text-primary transition-colors text-sm">support@afriqoma.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate uppercase tracking-wider">Newsletter</p>
                  <a href="mailto:newsletter@afriqoma.com" className="text-white hover:text-primary transition-colors text-sm">newsletter@afriqoma.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-xs text-slate uppercase tracking-wider">Phone</p>
                  <a href="tel:+18352750695" className="text-white hover:text-primary transition-colors text-sm">+1 835 275 0695</a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-xs text-slate uppercase tracking-wider">Headquarters</p>
                  <p className="text-white">Delaware, U.S.A</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-slate text-sm">&copy; 2026 Afriqoma Global Trade Hub. All rights reserved.</p>
            <p className="text-gold/80 text-xs mt-1 font-medium">Formerly Afritrade.bz</p>
            <p className="text-slate/70 text-xs mt-1">Registered in Delaware, U.S.A | License # 2026709254</p>
            <p className="text-slate/70 text-xs">African Company Registration No. 9555700</p>
          </div>
          <div className="flex gap-6 text-slate text-sm">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
